import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useLoginStore from 'components/login/useLoginStore';
import { convertBase64ToFile, convertFileToBase64 } from 'helpers/file.helper';
import { Modal, OpImageUploader } from 'op2mise-react-widgets';
import OpTextBox from 'op2mise-react-widgets/op-textbox/OpTextBox';
import Switch from 'shared/components/switch/Switch';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';
import {
  getProfileGeneralDataAsync,
  updateProfileGeneralDataAsync,
} from 'services/user-profile';
import Select from 'shared/components/select/Select';
import { PhoneLocationOptions, PhoneTypeOptions } from './generalForm.constant';
import { validateGeneralForm } from './generalForm.helper';
import styles from './GeneralForm.module.css';

export const GeneralForm = forwardRef(({ setIsDirty }, ref) => {
  const { setProfilePicture, setUsername, setJobTitle } = useLoginStore(
    (state) => state,
  );

  const DEFAULT_PHONE_LOCATION = PhoneLocationOptions[0].name;
  const DEFAULT_PHONE_TYPE = PhoneTypeOptions[0].name;
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    orderContactRole: '',
    phoneLocation: DEFAULT_PHONE_LOCATION,
    phoneType: DEFAULT_PHONE_TYPE,
    areaCode: '',
    countryCode: '',
    phoneNumber: '',
    isEmailNotificationEnabled: true,
    profilePhoto: null,
    fileExtension: '',
    fileChanged: false,
  });

  const [pristineData, setPristineData] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadDataAsync = async () => {
      try {
        setIsLoading(true);
        const result = await getProfileGeneralDataAsync();

        if (result) {
          const defaultProfilePhotoName = result.profilePhotoName || 'profile-photo';
          const photoFile = convertBase64ToFile(
            result.profilePhoto,
            defaultProfilePhotoName,
          );
          const initialData = {
            ...result,
            phoneLocation: result.phoneLocation ?? DEFAULT_PHONE_LOCATION,
            phoneType: result.phoneType ?? DEFAULT_PHONE_TYPE,
            profilePhoto: photoFile,
          };

          setData(initialData);
          setPristineData(initialData);
        }
      } catch (error) {
        setApiError(`Failed to fetch profile general data: ${error}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadDataAsync();
  }, [DEFAULT_PHONE_LOCATION, DEFAULT_PHONE_TYPE]);

  useImperativeHandle(ref, () => ({
    save: async () => {
      setApiError('');
      if (!validateGeneralForm(data, setErrors)) return false;

      try {
        let base64Photo = null;
        let fileExt = null;

        if (data.fileChanged) {
          base64Photo = await convertFileToBase64(data.profilePhoto);
          fileExt = data.fileExtension || '';
        }

        const payloadToSave = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          jobTitle: data.jobTitle,
          orderContactRole: data.orderContactRole,
          phoneLocation: data.phoneLocation,
          phoneType: data.phoneType,
          countryCode: data.countryCode,
          areaCode: data.areaCode,
          phoneNumber: data.phoneNumber,
          isEmailNotificationEnabled: data.isEmailNotificationEnabled,
        };

        if (data.fileChanged) {
          payloadToSave.profilePhoto = base64Photo;
          payloadToSave.fileExtension = fileExt;
          payloadToSave.isProfilePhotoChanged = data.fileChanged;
        }

        await updateProfileGeneralDataAsync(payloadToSave);

        setUsername(`${data.firstName} ${data.lastName}`);
        setJobTitle(data.jobTitle);
        if (data.fileChanged) {
          setProfilePicture(base64Photo);
        }

        setPristineData({ ...data, fileChanged: false });
        setData((prev) => ({ ...prev, fileChanged: false }));
        return true;
      } catch (error) {
        setApiError(`Failed to update profile settings: ${error}`);
        return false;
      }
    },
    reset: () => {
      if (pristineData) {
        setData(pristineData);
        setErrors({});
        setApiError('');
      }
    },
  }));

  const handleFieldValue = (field, value) => {
    let { fileChanged, fileExtension } = data;
    let safeValue = value;

    if (field === 'profilePhoto') {
      fileExtension = value?.name?.split('.').pop()?.toLowerCase() || '';
      fileChanged = true;
      safeValue = value;
    } else if (['phoneLocation', 'phoneType'].includes(field)) {
      safeValue = value.name;
    } else if (value && typeof value === 'object') {
      if (value.value !== undefined) safeValue = value.value;
      else if (value.target !== undefined) safeValue = value.target.value;
    }

    return { safeValue, fileExtension, fileChanged };
  };

  const handleChange = (field, value) => {
    const result = handleFieldValue(field, value);

    setData((prev) => ({
      ...prev,
      [field]: result.safeValue,
      fileExtension: result.fileExtension,
      fileChanged: result.fileChanged,
    }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setApiError('');
  };

  if (isLoading) return <BootstrapSpinner pushTop="30px" />;

  return (
    <div className={clsx(styles.generalForm, 'container')}>
      <section className={styles.imageSection}>
        <p className={styles.sectionTitle}>Profile Photo</p>
        <div className="row gx-2 mt-2">
          <div className="col col-12 col-lg-6">
            <OpImageUploader
              onChange={(val) => handleChange('profilePhoto', val)}
              value={data.profilePhoto}
            />
            <p className={styles.errorMessage}>{errors.profilePhoto}</p>
          </div>
        </div>
      </section>

      <section className={styles.personalDetailsSection}>
        <p className={styles.sectionTitle}>Personal Details</p>
        <div className="row gx-2 mb-3">
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.firstName}
              label="First Name"
              maxLength="100"
              onChange={(val) => handleChange('firstName', val)}
              placeholder="Enter First Name"
              showAsterisk
              value={data.firstName}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.lastName}
              label="Last Name"
              maxLength="100"
              onChange={(val) => handleChange('lastName', val)}
              placeholder="Enter Last Name"
              showAsterisk
              value={data.lastName}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.email}
              label="Email"
              maxLength="254"
              onChange={(val) => handleChange('email', val)}
              placeholder="Enter Email"
              showAsterisk
              value={data.email}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              label="Job Title"
              maxLength="100"
              onChange={(val) => handleChange('jobTitle', val)}
              placeholder="Enter Job Title"
              value={data.jobTitle}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.orderContactRole}
              label="Order Contact Role"
              maxLength="100"
              onChange={(val) => handleChange('orderContactRole', val)}
              placeholder="Enter Order Contact Role"
              showAsterisk
              value={data.orderContactRole}
            />
          </div>
        </div>
      </section>

      <section className={styles.phoneSection}>
        <p className={styles.sectionTitle}>Phone</p>
        <div className="row mb-4 gx-2 mt-3">
          <div className="col-12 col-md-3">
            <label htmlFor="phone-location" className={styles.phoneLocationLabel}>
              Phone Location
            </label>
            <Select
              id="phone-location"
              list={PhoneLocationOptions}
              noSearch
              onSelect={(val) => handleChange('phoneLocation', val)}
              text={data.phoneLocation}
              width="100%"
            />
          </div>
          <div className="col-12 col-md-2">
            <label htmlFor="phone-type" className={styles.phoneTypeLabel}>
              Phone Type
            </label>
            <Select
              id="phone-type"
              list={PhoneTypeOptions}
              noSearch
              onSelect={(val) => handleChange('phoneType', val)}
              text={data.phoneType}
              width="100%"
            />
          </div>
          <div className="col-12 col-md-2">
            <OpTextBox
              errorMessage={errors.countryCode}
              label="Country Code"
              maxLength="10"
              onChange={(val) => handleChange('countryCode', val)}
              placeholder="Enter Country Code"
              showAsterisk
              value={data.countryCode}
            />
          </div>
          <div className="col-12 col-md-2">
            <OpTextBox
              enableNumbersOnly
              errorMessage={errors.areaCode}
              label="Area Code"
              maxLength="10"
              onChange={(val) => handleChange('areaCode', val)}
              placeholder="Enter Area Code"
              showAsterisk
              value={data.areaCode}
            />
          </div>
          <div className="col-12 col-md-2">
            <OpTextBox
              enableNumbersOnly
              errorMessage={errors.phoneNumber}
              label="Phone Number"
              maxLength="20"
              onChange={(val) => handleChange('phoneNumber', val)}
              placeholder="Enter Phone Number"
              showAsterisk
              value={data.phoneNumber}
            />
          </div>
        </div>
      </section>

      <section className={styles.notificationSection}>
        <p className={styles.sectionTitle}>Notifications</p>
        <div className="row gx-2">
          <div className="col-12">
            <Switch
              active={data.isEmailNotificationEnabled}
              label="Email notifications"
              onSwitch={(val) => handleChange('isEmailNotificationEnabled', val)}
            />
          </div>
        </div>
      </section>

      {apiError && (
        <Modal handleOnClose={() => setApiError('')}>
          <p
            className="content-emphasized"
            style={{ fontSize: '16px', textAlign: 'center' }}
          >
            {apiError}
          </p>
        </Modal>
      )}
    </div>
  );
});

GeneralForm.displayName = 'GeneralForm';

GeneralForm.propTypes = {
  setIsDirty: PropTypes.func,
};

GeneralForm.defaultProps = {
  setIsDirty: undefined,
};

export default GeneralForm;
